import uuid from 'uuid';
import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import {
  FieldSet,
  TextCollectionFieldType,
  TextCollectionAttachment,
} from '@scale/shared/types/params/textcollection';
import {
  FieldSetAnnotation,
  TextCollectionAnnotation,
  TextCollectionResponse,
} from '@scale/shared/types/response/textcollection';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles, FormHelperText, Tooltip, Button, ClickAwayListener } from '@material-ui/core';
import { Breadcrumb } from './breadcrumbs';
import {
  getFieldSetTitle,
  getFieldSetCounts,
  getFieldValidations,
  isFieldSet,
  getFirstTextField,
  getChildFieldMissingDetailCounts,
} from './fieldSetUtils';
import { isBlankField, removeHiddenFields } from '@scale/shared/textcollection/helpers';
// @ts-ignore JS package
import pluralize from 'pluralize';
import { getHelperText } from './helperText';
import FieldPreview from './FieldPreview';
import FieldSetForm from './FieldSetForm';
import {
  getAnnotationResponses,
  getFieldMaxResponses,
} from '@scale/shared/textcollection/defaults';
import { AutoCompleteQuery } from './autoComplete';
import { IconButton } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface FieldSetSectionProps {
  field: FieldSet;
  annotation: FieldSetAnnotation;
  setAnnotation: (annotation: FieldSetAnnotation) => void;
  disabled?: boolean;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
  quickAddResponses: Record<string, TextCollectionAnnotation>[];
  autoComplete: (query: AutoCompleteQuery) => Record<string, TextCollectionAnnotation>[];
  previouslyVisitedChild?: Breadcrumb;
  attachment: TextCollectionAttachment;
  previousResponses?: TextCollectionResponse[];
}

export default function FieldSetSection(props: FieldSetSectionProps) {
  const styles = useFieldSetStyles();
  const [showForm, setShowForm] = React.useState<boolean>(false);
  const scrollToPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const [didScrollToPreview, setDidScrollToPreview] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!didScrollToPreview && scrollToPreviewRef.current) {
      scrollToPreviewRef.current.scrollIntoView();
      setDidScrollToPreview(true);
    }
  }, [didScrollToPreview, scrollToPreviewRef.current, setDidScrollToPreview]);

  let firstTextField: any;
  try {
    firstTextField = getFirstTextField(props.field);
  } catch (_e) {
    firstTextField = { field_id: null };
  }
  const fieldSetChildFields = props.field.fields.filter(
    field => !isFieldSet(field) && field.field_id !== firstTextField?.field_id,
  );
  const inlineFormFields: FieldSet[] = props.field.fields.filter(
    field => isFieldSet(field) && field.inline && field.field_id !== firstTextField?.field_id,
  ) as FieldSet[];

  // We don't want to recompute this when fields at this level change;
  // this feature only depends on child values, which aren't accessible
  // from this level.
  const missingDetailCounts: Record<string, number>[] = React.useMemo(
    () =>
      props.annotation.response.map(annotation =>
        getChildFieldMissingDetailCounts(annotation, props.field),
      ),
    [props.field],
  );

  const handleRemove = (index: number) => {
    const response = props.annotation.response.slice();
    response.splice(index, 1);
    const uuids = props.annotation.uuids?.slice();
    if (uuids) {
      uuids.splice(index, 1);
    }
    const fieldSetAnnotation: FieldSetAnnotation = {
      type: TextCollectionFieldType.FieldSet,
      field_id: props.field.field_id,
      response,
      uuids,
    };
    props.setAnnotation(fieldSetAnnotation);
  };

  const responseItems = props.annotation.response.map((annotations, index) => {
    const title = getFieldSetTitle(props.field, annotations);
    const onClick = () => {
      const breadcrumb: Breadcrumb = {
        id: props.field.field_id,
        index,
      };
      props.addBreadcrumb(breadcrumb);
    };
    const validations = getFieldValidations(props.field, annotations);
    const filledFields = fieldSetChildFields.filter(field => {
      return !validations[field.field_id].isBlank;
    });
    const filledLabels = filledFields.map((field, index) => {
      const label = field.title;
      if (index > 0) {
        return label.toLowerCase();
      } else {
        return label;
      }
    });
    const visibleAnnotations = removeHiddenFields({ annotations }, props.field.fields).annotations;
    const requiredFields = props.field.fields.filter(field => field.required);
    const missingCount = requiredFields.filter(
      field => isBlankField(annotations[field.field_id]) && visibleAnnotations[field.field_id],
    ).length;
    const fieldSetCounts = getFieldSetCounts(props.field.fields, annotations).filter(
      fieldSetCount => fieldSetCount.count > 0,
    );
    const childMissingDetails = missingDetailCounts[index];
    const isMissing = !!missingCount || !_.isEmpty(childMissingDetails);
    const isPreviouslyVisited =
      props.field.field_id === props.previouslyVisitedChild?.id &&
      index === props.previouslyVisitedChild?.index;
    const fieldSetItem = (
      <div
        key={index}
        className={cn({
          [styles.fieldSetResponse]: true,
          [styles.highlighted]: isPreviouslyVisited,
        })}
        onClick={onClick}
        ref={isPreviouslyVisited ? scrollToPreviewRef : null}
      >
        <div>
          <div className={styles.fieldSetTitle}>{title}</div>
          {!isMissing && fieldSetChildFields.length > 0 && filledFields.length === 0 && (
            <div className={styles.warnBlank}>No details</div>
          )}
          {!isMissing && filledFields.length > 0 && (
            <div className={styles.hintFields}>{filledLabels.join(', ')}</div>
          )}
          {_.isEmpty(childMissingDetails) && !!missingCount && (
            <div className={styles.warnMissing}>Missing details</div>
          )}
          {!_.isEmpty(childMissingDetails) && (
            <div className={styles.warnMissing}>
              {_.entries(childMissingDetails)
                .map(([k, v]) => `${v} ${_.lowerCase(pluralize(k, v))} missing details`)
                .join(', ')}
            </div>
          )}
        </div>
        <div className={styles.fieldSetChildren}>
          {fieldSetCounts.slice(0, 1).map(fieldSetCount => {
            const label = pluralize(fieldSetCount.title.toLowerCase(), fieldSetCount.count, false);
            const labelMore = fieldSetCounts.length > 1 ? label + ' and more' : label;
            return (
              <span key={fieldSetCount.id}>
                {fieldSetCount.count} {labelMore}
              </span>
            );
          })}
        </div>
        <IconButton
          color="secondary"
          disabled={props.disabled}
          onClick={e => {
            e.stopPropagation();
            handleRemove(index);
          }}
        >
          <FontAwesomeIcon size="sm" icon="trash" />
        </IconButton>
        <div className={styles.fieldSetIcon}>
          <ChevronRight />
        </div>
      </div>
    );
    const unitFieldPreviews = fieldSetChildFields
      .filter(childField => {
        return !!annotations[childField.field_id] && visibleAnnotations[childField.field_id];
      })
      .map(childField => {
        return (
          <FieldPreview
            key={childField.field_id}
            field={childField}
            annotation={annotations[childField.field_id]}
            editDisabled={true}
            attachment={props.attachment}
          />
        );
      });
    const inlineFieldPreviews = inlineFormFields
      .filter(formField => {
        return !!annotations[formField.field_id];
      })
      .map((formField: FieldSet) => {
        const subFields = formField.fields;
        const fieldSetAnnotation: FieldSetAnnotation = annotations[
          formField.field_id
        ] as FieldSetAnnotation;
        const responses: Record<string, TextCollectionAnnotation>[] = fieldSetAnnotation.response;
        return responses.map((fieldResponse, index) => {
          return subFields.map(subField => {
            const annotation = fieldResponse[subField.field_id];
            if (
              !annotation ||
              annotation.type === TextCollectionFieldType.FieldSet ||
              !annotation.response?.[0]
            ) {
              return null;
            }
            return (
              <FieldPreview
                key={`${formField.field_id}-${subField.field_id}-${index}`}
                field={subField}
                annotation={annotation}
                editDisabled={true}
                attachment={props.attachment}
              />
            );
          });
        });
      });
    const fieldPreviews = _.filter(_.flatMap([...unitFieldPreviews, inlineFieldPreviews]));
    if (!fieldPreviews.length) {
      return fieldSetItem;
    }
    return (
      <Tooltip
        placement="top"
        classes={{ tooltip: styles.tooltip }}
        key={index}
        title={<>{fieldPreviews}</>}
        enterDelay={200}
        leaveDelay={0}
      >
        {fieldSetItem}
      </Tooltip>
    );
  });

  const responses = getAnnotationResponses(props.annotation);
  const maxResponses = getFieldMaxResponses(props.field);
  const helperText = getHelperText({
    description: props.field.description,
  });

  const addResponse = function (fieldSetResponse: Record<string, TextCollectionAnnotation>): void {
    const response = props.annotation.response.slice();
    response.push(fieldSetResponse);
    const uuids = props.annotation.uuids?.slice();
    if (uuids) {
      uuids.push(uuid.v4());
    }
    const fieldSetAnnotation: FieldSetAnnotation = {
      type: TextCollectionFieldType.FieldSet,
      field_id: props.field.field_id,
      response,
      uuids,
    };
    props.setAnnotation(fieldSetAnnotation);
  };

  const addNewResponse = () => {
    addResponse({});
    const breadcrumb: Breadcrumb = {
      id: props.field.field_id,
      index: props.annotation.response.length,
    };
    props.addBreadcrumb(breadcrumb);
  };

  return (
    <div className={styles.fieldSet}>
      <div className={styles.title}>{pluralize(props.field.title, 2, false)}</div>
      {helperText ? (
        <FormHelperText classes={{ root: styles.fieldSetHelperText }}>{helperText}</FormHelperText>
      ) : null}
      {responseItems}
      {responses < maxResponses && (
        <ClickAwayListener
          onClickAway={() => {
            setShowForm(false);
          }}
        >
          <div style={{ display: props.disabled ? 'none' : 'block' }}>
            <div className={styles.addNewActions}>
              <Button
                type="button"
                classes={{ root: styles.addButton, label: styles.addButtonText }}
                startIcon={<Plus />}
                color="primary"
                variant="text"
                disabled={showForm}
                onClick={addNewResponse}
              >
                Add {props.field.title}
              </Button>
              {props.quickAddResponses.map(response => {
                const title = getFieldSetTitle(props.field as FieldSet, response);
                const onClick = () => {
                  addResponse(_.cloneDeep(response));
                };
                return (
                  <Button
                    key={`quick add ${title}`}
                    type="button"
                    variant="outlined"
                    classes={{ root: styles.quickAddButton, label: styles.quickAddButtonText }}
                    onClick={onClick}
                  >
                    Add &ldquo;{title}&rdquo;
                  </Button>
                );
              })}
            </div>
            {showForm && (
              <FieldSetForm
                field={props.field}
                annotation={props.annotation}
                addResponse={addResponse}
                autoComplete={props.autoComplete}
              />
            )}
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
}

const useFieldSetStyles = makeStyles({
  fieldSet: {
    paddingTop: '16px',
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  fieldSetResponse: {
    background: '#FFFFFF',
    boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'box-shadow 200ms ease-out',
    '&:hover': {
      boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.15), 0px 2px 6px rgba(0, 0, 0, 0.15)',
    },
    '&:hover > div > svg': {
      transform: 'translateX(6px)',
    },
  },
  highlighted: {
    background: '#FFFDE7',
  },
  fieldSetTitle: {
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '20px',
  },
  fieldSetHelperText: {
    marginBottom: '16px',
  },
  fieldSetChildren: {
    marginLeft: 'auto',
    color: '#808080',
    fontSize: '13px',
    lineHeight: '20px',
    flexShrink: 0,
  },
  fieldSetIcon: {
    color: '#2B99D8',
    marginLeft: 8,
    '& svg': {
      transition: 'transform 200ms ease-out',
    },
  },
  warnMissing: {
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    color: '#EA5F28',
    display: 'inline-block',
  },
  warnBlank: {
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    background: 'rgb(248, 187, 6, 0.3)',
    borderRadius: '2px',
    padding: '0 4px',
    color: 'rgba(0, 0, 0, 0.6)',
    display: 'inline-block',
  },
  hintFields: {
    fontSize: '11px',
    lineHeight: '16px',
    letterSpacing: '0.1px',
    color: '#808080',
    maxWidth: '285px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  tooltip: {
    display: 'block',
    width: '320px',
    background: '#FFFFFF',
    padding: '8px',
    boxShadow: '0px 6px 24px rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    color: '#000000',
  },
  addNewActions: {
    marginTop: '8px',
    marginBottom: '8px',
    overflowX: 'auto',
    whitespace: 'nowrap',
  },
  addButton: {
    marginRight: 8,
    marginBottom: 8,
  },
  addButtonText: {
    color: '#2B99D8',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  quickAddButton: {
    marginRight: 8,
    marginBottom: 8,
  },
  quickAddButtonText: {
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    textTransform: 'none',
  },
});

function ChevronRight() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.6603 18.1711C10.3674 18.4644 9.89213 18.4647 9.59883 18.1718C9.30552 17.8788 9.30524 17.4036 9.59819 17.1103L14.7006 12.014L9.59819 6.91776C9.30524 6.62445 9.30552 6.1492 9.59883 5.85625C9.89213 5.56329 10.3674 5.56358 10.6603 5.85688L16.2937 11.4836C16.5867 11.7759 16.5872 12.2504 16.2948 12.5434C16.2945 12.5437 16.2941 12.5441 16.2937 12.5445L10.6603 18.1711Z"
        fill="#2B99D8"
      />
    </svg>
  );
}

function Plus() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8 6.80039H9.20003V3.20039C9.20003 2.75864 8.84177 2.40039 8.40002 2.40039H7.60002C7.15827 2.40039 6.80002 2.75864 6.80002 3.20039V6.80039H3.20002C2.75827 6.80039 2.40002 7.15864 2.40002 7.60039V8.40039C2.40002 8.84214 2.75827 9.20039 3.20002 9.20039H6.80002V12.8004C6.80002 13.2421 7.15827 13.6004 7.60002 13.6004H8.40002C8.84177 13.6004 9.20003 13.2421 9.20003 12.8004V9.20039H12.8C13.2418 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2418 6.80039 12.8 6.80039Z"
        fill="#2B99D8"
      />
    </svg>
  );
}
